/* eslint-disable no-nested-ternary */
import React from "react";
import PropTypes from "prop-types";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import Divider from "@mui/material/Divider";
import { Box, Tooltip } from "@mui/material";
import { white } from "../../theme/color.constants";
import ErrorBoundary from "../../ErrorBoundary";

const accordion = {
  ".accordion .MuiAccordionSummary-root": {
    "&.Mui-expanded": {
      margin: "0.125rem",
      minHeight: "unset",
    },
    borderBottom: "3px solid #43b73f",
    borderTop: "3px solid #43b73f",
    minHeight: "unset",
    "& .MuiAccordionSummary-content": {
      margin: "0rem",
      "&.Mui-expanded": { margin: "0rem" },
    },
  },
};
function AlertDialog(props) {
  const {
    onCancel = () => {},
    title = "",
    content = "",
    buttonArray = [],
    onOk = () => {},
    // eslint-disable-next-line react/jsx-no-useless-fragment
    icon: Icon = <></>,
    showTitle = true,
    showActionBtn = false,
    hideIcon,
    customBgColor,
    progress = false,
    customTitleColor,
  } = props;

  const defaultButtons = [
    {
      id: 1,
      name: "No",
      icon: undefined,
      variant: "contained",
      onClickHandler: onCancel,
      show: true,
      disabled: progress,
    },
    {
      id: 2,
      name: "Yes",
      icon: undefined,
      variant: "contained",
      onClickHandler: onOk,
      show: true,
      disabled: progress,
    },
  ];

  const actionButtons = buttonArray.length > 0 ? buttonArray : defaultButtons;
  const bgColor = (theme) => {
    if (!showTitle) return "transparent";
    if (theme.palette.mode === "light") {
      if (customBgColor) return customBgColor.header;
      return theme.palette.primary.light;
    }
    if (customBgColor) return customBgColor.header;
    return theme.palette.primary.dark;
  };
  return (
    <>
      <div
        role="alert"
        aria-live="polite"
        aria-label="Dialog opened click tab to navigate between Dialog"
      />
      {title && (
        <Box
          sx={[
            (theme) => ({
              backgroundColor: bgColor(theme),
              color: !showTitle
                ? undefined
                : customTitleColor
                ? customTitleColor.color
                : white,
              pr: 3,
              display: "flex",
              alignItems: "center",
            }),
            showTitle ? { mb: 2 } : { mb: 0 },
          ]}
        >
          <Tooltip title={title}>
            <DialogTitle
              id="dialog-title"
              sx={{
                width: "90%",
                textOverflow: "ellipsis",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                whiteSpace: "nowrap",
                minHeight: "1.5625rem",
              }}
            >
              {Icon}
              {title}
            </DialogTitle>
          </Tooltip>
          {showTitle && (
            <IconButton
              aria-label="Close dialog"
              color="inherit"
              onClick={onCancel}
              sx={[
                {
                  position: "absolute",
                  top: 10,
                  right: 10,
                  cursor: "pointer",
                  display: hideIcon,
                },
                customTitleColor
                  ? { color: customTitleColor.color }
                  : { color: white },
              ]}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      )}
      {content && (
        <DialogContent
          id="dialog-description"
          sx={{ py: "0.8em", px: "0.8em", minHeight: "3rem" }}
        >
          {content}
        </DialogContent>
      )}
      <Divider aria-hidden="true" />
      {(showActionBtn || !showTitle) && (
        <DialogActions
          sx={(theme) => ({
            my: 1,
            "& .MuiButton-startIcon>*": {
              fontSize: "1.7em",
              mr: "-0.2em",
            },
            [theme.breakpoints.down("sm")]: {
              justifyContent: "center",
              flexDirection: "column",
              "& .MuiButton-root": {
                minWidth: "100%",
                mx: 0,
              },
              "& .MuiButton-root:first-of-type": {
                mb: 1.5,
              },
            },
          })}
        >
          {actionButtons
            .filter((item) => item.show)
            .map(({ id, name, icon, variant, onClickHandler, disabled }) => (
              <Button
                key={id}
                onClick={onClickHandler || undefined}
                variant={variant}
                startIcon={icon}
                disabled={disabled || undefined}
              >
                {name}
              </Button>
            ))}
        </DialogActions>
      )}
    </>
  );
}

function DialogPopup(props) {
  const {
    openPopup = false,
    onCancel = () => {},
    maxWidth = "sm",
    paperWidth = "60%",
    showTitle = true,
    height,
    customBgColor,
  } = props;
  if (!openPopup) return null;

  return (
    <Dialog
      data-testid="custom-dialog"
      open={openPopup}
      onClose={onCancel}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      maxWidth={maxWidth}
      PaperProps={{
        sx: (theme) => ({
          ...accordion,
          width: theme.breakpoints.down("sm") ? "90%" : paperWidth,
          height,
          backgroundColor: customBgColor ? customBgColor.body : undefined,
        }),
        tabIndex: showTitle ? 0 : -1,
      }}
    >
      <ErrorBoundary key="dialog">
        <AlertDialog
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      </ErrorBoundary>
    </Dialog>
  );
}

export default DialogPopup;

AlertDialog.propTypes = {
  progress: PropTypes.bool,
  title: PropTypes.string,
  onOk: PropTypes.func,
  onCancel: PropTypes.func,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  buttonArray: PropTypes.oneOfType([PropTypes.array]),
  icon: PropTypes.oneOfType([PropTypes.element]),
  showTitle: PropTypes.bool,
  showActionBtn: PropTypes.bool,
  hideIcon: PropTypes.string,
  customBgColor: PropTypes.shape({
    header: PropTypes.string,
    body: PropTypes.string,
  }),
};
AlertDialog.defaultProps = {
  progress: false,
  title: "",
  content: "",
  onCancel: () => {},
  onOk: () => {},
  buttonArray: [],
  showTitle: true,
  showActionBtn: false,
  hideIcon: undefined,
  customBgColor: undefined,
  // eslint-disable-next-line react/jsx-no-useless-fragment
  icon: <></>,
};

DialogPopup.propTypes = {
  openPopup: PropTypes.bool,
  onCancel: PropTypes.func,
  maxWidth: PropTypes.string,
  paperWidth: PropTypes.string,
  showTitle: PropTypes.bool,
  height: PropTypes.string,
  customBgColor: PropTypes.shape({
    header: PropTypes.string,
    body: PropTypes.string,
  }),
};

DialogPopup.defaultProps = {
  openPopup: false,
  onCancel: () => {},
  maxWidth: "sm",
  paperWidth: "60%",
  showTitle: true,
  height: undefined,
  customBgColor: undefined,
};
